/**
 * PermissionMatrix Component
 *
 * Displays permissions organized by category with checkboxes for selection.
 * Uses real API data (permission catalog) with i18next-lokalisoituja nimiä.
 */

import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Paper,
  Stack,
  Chip,
  CircularProgress,
} from '@mui/material';
import {
  usePermissionsByCategory,
  getPermissionCategories,
  type PermissionCategory,
  type Permission,
} from '@/hooks';

interface PermissionMatrixProps {
  selectedPermissionIds: string[];
  onChange: (permissionIds: string[]) => void;
  disabled?: boolean;
}

const CATEGORY_LABEL_KEYS: Record<PermissionCategory, string> = {
  dashboard: 'roles.category.dashboard',
  operations: 'roles.category.operations',
  customers: 'roles.category.customers',
  inventory: 'roles.category.inventory',
  communication: 'roles.category.communication',
  reports: 'roles.category.reports',
  hr: 'roles.category.hr',
  administration: 'roles.category.administration',
};

export function PermissionMatrix({
  selectedPermissionIds,
  onChange,
  disabled = false,
}: PermissionMatrixProps) {
  const { t } = useTranslation('settings');
  const categories = getPermissionCategories();
  const { data: permissionsByCategory, isLoading } = usePermissionsByCategory();

  if (isLoading || !permissionsByCategory) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress />
      </Box>
    );
  }

  const handlePermissionToggle = (permissionId: string) => {
    if (selectedPermissionIds.includes(permissionId)) {
      onChange(selectedPermissionIds.filter((id) => id !== permissionId));
    } else {
      onChange([...selectedPermissionIds, permissionId]);
    }
  };

  const handleCategoryToggle = (categoryPermissions: Permission[]) => {
    const categoryPermissionIds = categoryPermissions.map((p) => p.id);
    const allSelected = categoryPermissionIds.every((id) =>
      selectedPermissionIds.includes(id)
    );

    if (allSelected) {
      onChange(
        selectedPermissionIds.filter((id) => !categoryPermissionIds.includes(id))
      );
    } else {
      const newIds = new Set([...selectedPermissionIds, ...categoryPermissionIds]);
      onChange(Array.from(newIds));
    }
  };

  const getCategoryStats = (categoryPermissions: Permission[]) => {
    const selectedCount = categoryPermissions.filter((p) =>
      selectedPermissionIds.includes(p.id)
    ).length;
    return { selectedCount, totalCount: categoryPermissions.length };
  };

  const totalSelected = selectedPermissionIds.length;
  const totalPermissions = Object.values(permissionsByCategory).flat().length;

  return (
    <Stack spacing={2}>
      {categories.map((category) => {
        const permissions = permissionsByCategory[category] ?? [];
        if (permissions.length === 0) return null;

        const { selectedCount, totalCount } = getCategoryStats(permissions);
        const isFullySelected = selectedCount === totalCount && totalCount > 0;
        const isPartiallySelected = selectedCount > 0 && selectedCount < totalCount;

        return (
          <Paper key={category} variant="outlined" sx={{ p: 2 }}>
            <Stack spacing={1.5}>
              {/* Category header with select all */}
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isFullySelected}
                      indeterminate={isPartiallySelected}
                      onChange={() => handleCategoryToggle(permissions)}
                      disabled={disabled}
                    />
                  }
                  label={
                    <Typography variant="subtitle1" fontWeight="medium">
                      {t(CATEGORY_LABEL_KEYS[category])}
                    </Typography>
                  }
                />
                <Chip
                  label={`${selectedCount}/${totalCount}`}
                  size="small"
                  color={selectedCount > 0 ? 'primary' : 'default'}
                  variant="outlined"
                />
              </Box>

              {/* Individual permissions */}
              <FormGroup sx={{ pl: 4 }}>
                {permissions.map((permission) => (
                  <FormControlLabel
                    key={permission.id}
                    control={
                      <Checkbox
                        checked={selectedPermissionIds.includes(permission.id)}
                        onChange={() => handlePermissionToggle(permission.id)}
                        disabled={disabled}
                        size="small"
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2">{t(permission.nameKey)}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {t(permission.descKey)}
                        </Typography>
                      </Box>
                    }
                    sx={{ mb: 0.5 }}
                  />
                ))}
              </FormGroup>
            </Stack>
          </Paper>
        );
      })}

      {/* Summary */}
      <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.default' }}>
        <Typography variant="body2" color="text.secondary">
          <strong>{totalSelected}</strong> / {totalPermissions} {t('rolesManagement.permissionsSelected')}
        </Typography>
      </Paper>
    </Stack>
  );
}
